import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit, Save, X, Building2, MapPin, Mail, Phone, Receipt, CreditCard } from 'lucide-react';

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export const OrganizationBasicInfo = ({
  user,
  userData,
  organizationData,
  loading: orgLoading,
  updateOrganization,
  createOrganization,
  isOwner,
  hasOrganization,
}) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [success, setSuccess] = useState(null);

  const blankForm = () => ({
    // ── General ──────────────────────────────────────────────────────────
    name:               organizationData?.name                   || '',
    description:        organizationData?.description            || '',
    email:              organizationData?.email                  || '',
    phone:              organizationData?.phone                  || '',
    postalCode:         organizationData?.postalCode             || '',
    locationAddress:    organizationData?.location?.address      || '',
    locationPostalCode: organizationData?.location?.postalCode   || '',

    // ── billing — org as CLIENT being billed by couriers ─────────────
    // These details appear on invoices that couriers send TO the org.
    billingCompanyName: organizationData?.billing?.companyName || '',
    billingYNumber:     organizationData?.billing?.yNumber     || '',
    billingReference:   organizationData?.billing?.reference   || '',
    billingDepartment:  organizationData?.billing?.department  || '',

    // ── payment — org as SERVICE PROVIDER being paid by clients ──────
    // These details appear on invoices that the org sends TO clients.
    paymentCompanyName: organizationData?.payment?.companyName || '',
    paymentYNumber:     organizationData?.payment?.yNumber     || '',
    paymentIban:        organizationData?.payment?.iban        || '',
    paymentAlvRate:     organizationData?.payment?.alvRate     ?? 25.5,
  });

  const [formData, setFormData] = useState(blankForm);

  useEffect(() => {
    if (organizationData) setFormData(blankForm());
  }, [organizationData]);

  const handleChange = (field) => (e) => {
    setFormData((p) => ({ ...p, [field]: e.target.value }));
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setFormData(blankForm());
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Organisaation nimi vaaditaan');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const location = {};
      if (formData.locationAddress.trim())    location.address    = formData.locationAddress.trim();
      if (formData.locationPostalCode.trim()) location.postalCode = formData.locationPostalCode.trim();
      if (organizationData?.location?.coordinates) location.coordinates = organizationData.location.coordinates;

      const orgData = {
        name:        formData.name.trim(),
        description: formData.description.trim() || null,
        email:       formData.email.trim()        || null,
        phone:       formData.phone.trim()        || null,
        postalCode:  formData.postalCode.trim() ? Number(formData.postalCode.trim()) : null,
        location:    Object.keys(location).length > 0 ? location : null,

        // Who gets billed (org as client receiving courier invoices)
        billing: {
          companyName: formData.billingCompanyName.trim() || null,
          yNumber:     formData.billingYNumber.trim()     || null,
          reference:   formData.billingReference.trim()  || null,
          department:  formData.billingDepartment.trim() || null,
        },

        // Who gets paid (org as service provider sending invoices to clients)
        payment: {
          companyName: formData.paymentCompanyName.trim() || null,
          yNumber:     formData.paymentYNumber.trim()     || null,
          iban:        formData.paymentIban.trim()        || null,
          alvRate:     Number(formData.paymentAlvRate)    || 25.5,
        },
      };

      if (hasOrganization) {
        await updateOrganization(orgData);
      } else {
        await createOrganization(orgData);
      }

      setSuccess('Organisaation tiedot päivitetty');
      setEditing(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Tallennus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const disabled = !editing || loading;

  return (
    <Box>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        mb: 3, gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
            <Building2 size={22} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                {organizationData?.name || 'Ei organisaatiota'}
              </Typography>
              {hasOrganization && isOwner && (
                <Chip label="Omistaja" size="small" color="primary" />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {hasOrganization ? 'Organisaation perustiedot' : 'Luo organisaatio aloittaaksesi'}
            </Typography>
          </Box>
        </Box>

        {!editing && isOwner && (
          <Button
            variant="outlined"
            startIcon={<Edit size={16} />}
            onClick={() => setEditing(true)}
            fullWidth={isMobile}
          >
            {hasOrganization ? 'Muokkaa' : 'Luo organisaatio'}
          </Button>
        )}
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── General info ─────────────────────────────────────────────────── */}
      <SectionHeader
        icon={<Building2 size={18} />}
        title="Perustiedot"
        subtitle="Organisaation yleiset yhteystiedot"
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Organisaation nimi" fullWidth required size="small"
            value={formData.name} onChange={handleChange('name')} disabled={disabled}
            error={editing && !formData.name.trim()}
            helperText={editing && !formData.name.trim() ? 'Pakollinen kenttä' : ''}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Kuvaus" fullWidth multiline rows={2} size="small"
            value={formData.description} onChange={handleChange('description')} disabled={disabled}
            placeholder="Kerro lyhyesti organisaatiostasi..."
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Sähköposti" fullWidth type="email" size="small"
            value={formData.email} onChange={handleChange('email')} disabled={disabled}
            InputProps={{ startAdornment: <InputAdornment position="start"><Mail size={16} /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Puhelinnumero" fullWidth size="small"
            value={formData.phone} onChange={handleChange('phone')} disabled={disabled}
            InputProps={{ startAdornment: <InputAdornment position="start"><Phone size={16} /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Postinumero" fullWidth type="number" size="small"
            value={formData.postalCode} onChange={handleChange('postalCode')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Toimipisteen postinumero" fullWidth size="small"
            value={formData.locationPostalCode} onChange={handleChange('locationPostalCode')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Toimipisteen osoite" fullWidth size="small"
            value={formData.locationAddress} onChange={handleChange('locationAddress')} disabled={disabled}
            InputProps={{ startAdornment: <InputAdornment position="start"><MapPin size={16} /></InputAdornment> }}
          />
        </Grid>
      </Grid>

      {/* ── billing — org as CLIENT ───────────────────────────────────── */}
      <Divider sx={{ my: 3 }} />
      <SectionHeader
        icon={<Receipt size={18} />}
        title="Laskutustiedot"
        subtitle="Organisaation tiedot saapuvilla laskuilla — kuriirit käyttävät näitä laskuttaessaan organisaatiota"
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Yrityksen nimi" fullWidth size="small"
            value={formData.billingCompanyName} onChange={handleChange('billingCompanyName')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Y-tunnus" fullWidth size="small"
            value={formData.billingYNumber} onChange={handleChange('billingYNumber')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Viite / ostotilausnumero" fullWidth size="small"
            value={formData.billingReference} onChange={handleChange('billingReference')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Kustannuspaikka / osasto" fullWidth size="small"
            value={formData.billingDepartment} onChange={handleChange('billingDepartment')} disabled={disabled}
          />
        </Grid>
      </Grid>

      {/* ── payment — org as SERVICE PROVIDER ────────────────────────── */}
      <Divider sx={{ my: 3 }} />
      <SectionHeader
        icon={<CreditCard size={18} />}
        title="Maksutiedot"
        subtitle="Organisaation tiedot lähteville laskuilla — käytetään kun organisaatio laskuttaa asiakkaitaan"
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Yrityksen nimi" fullWidth size="small"
            value={formData.paymentCompanyName} onChange={handleChange('paymentCompanyName')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Y-tunnus" fullWidth size="small"
            value={formData.paymentYNumber} onChange={handleChange('paymentYNumber')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="IBAN" fullWidth size="small"
            value={formData.paymentIban} onChange={handleChange('paymentIban')} disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="ALV %" fullWidth type="number" size="small"
            value={formData.paymentAlvRate} onChange={handleChange('paymentAlvRate')} disabled={disabled}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>
      </Grid>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      {editing && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1.5,
          justifyContent: 'flex-end',
          mt: 3,
        }}>
          <Button
            variant="outlined" startIcon={<X size={16} />}
            onClick={handleCancel} disabled={loading} fullWidth={isMobile}
          >
            Peruuta
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <Save size={16} />}
            onClick={handleSave} disabled={loading} fullWidth={isMobile}
          >
            {hasOrganization ? 'Tallenna' : 'Luo ja tallenna'}
          </Button>
        </Box>
      )}
    </Box>
  );
};